import { Component, DestroyRef, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { map, distinctUntilChanged, filter } from 'rxjs/operators';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import { IntegrationModel } from 'src/app/models/api';
import { PolicyModel } from 'src/app/models/api/governance/policy.model';
import { RuleCategory, ViolationRule } from 'src/app/models/api/governance/violationRule.model';
import {
  PoliciesHttpService,
  RuleType,
} from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { ViolationRulesHttpService } from 'src/app/services/httpServices/governance/violationRulesHttpService/violation-rules-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import 'src/app/common/arrayUtils';
import { EnvironmentsHttpService } from 'src/app/services/httpServices/governance/environmentsHttpService/environments-http.service';
import { EnvironmentModel } from 'src/app/models/api/governance/environment.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { InLineIntegrationsHttpService } from 'src/app/services/httpServices/edgeIntegrationsHttpsService/in-line-integrations-http.service';
import { InLineIntegration } from 'src/app/models/api/inLineIntegration/inLineIntegration.model';

@Component({
  selector: 'app-policy',
  templateUrl: './policy.component.html',
  styleUrls: ['./policy.component.scss'],
})
export class PolicyComponent implements OnInit {
  public integrationId: string;
  public policyId: string | null;

  public violationRules = signal<ViolationRule[]>([]);
  public hygieneRules = signal<ViolationRule[]>([]);

  public policyModel: PolicyModel;
  public integrationModel?: IntegrationModel;

  public isLoading = signal<boolean>(false);
  public currentTabIndex$: Observable<number>;

  public environments = signal<EnvironmentModel[]>([]);

  public inlineIntegration?: InLineIntegration = undefined;

  public RuleTypeEnum = RuleType;

  public featureFlagTypes = FeatureFlagTypes;

  // tabs
  public tabs: ISidepanelTab[] = [
    { labelPath: 'policyPage.policy.violationRulesTab' },
    { labelPath: 'policyPage.policy.hygieneRulesTab' },
    // to be enabled when deliver custom policy
    // { labelPath: "policyPage.policy.exemptionRulesTab" },
    { labelPath: 'policyPage.policy.configurationTab' },
  ];
  private readonly _currentTabIndexSubject = new BehaviorSubject(0);

  constructor(
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _violationRulesHttpService: ViolationRulesHttpService,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _notificationService: NotificationsService,
    private readonly _storeService: StoreService,
    private readonly _environmentsHttpService: EnvironmentsHttpService,
    private readonly _inlineIntegrationsHttpService: InLineIntegrationsHttpService,
    protected featureFlagsService: FeatureFlagsService,
    private readonly _destroyRef: DestroyRef
  ) {
    this.currentTabIndex$ = this._currentTabIndexSubject.asObservable();
  }

  ngOnInit(): void {
    this._handleUrlQueryParamsOnChange();
  }

  public onTabSelect(selectedIndex: number) {
    this._currentTabIndexSubject.next(selectedIndex);
  }

  public backToPolicies() {
    this._router.navigate(['app/policies'], {
      queryParams: { integration: this.integrationId },
    });
  }

  public refreshPage() {
    this.isLoading.set(true);
    this._getViolationRules();
    this._getEnvironments();
  }

  private _handleUrlQueryParamsOnChange() {
    this._activatedRoute.queryParams.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (params) => {
        const tab = params['tab'];
        if (tab == 'configuration') {
          this.onTabSelect(this.tabs.length - 1);
        }
      },
    });

    this.isLoading.set(true);
    this._activatedRoute.queryParams
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        // Only emit when integration param changes, ignore other param changes
        map((params) => {
          return params['integration'];
        }),
        distinctUntilChanged(),
        filter((integration) => {
          // Only process if integration exists AND it's different from current
          return !!integration && integration !== this.integrationId;
        })
      )
      .subscribe({
        next: (integration) => {
          this.integrationId = integration;
          this.policyId = this._activatedRoute.snapshot.paramMap.get('id');

          this._getViolationRules();
          this._getEnvironments();
          if (this.featureFlagsService.isFeatureEnabled(FeatureFlagTypes.InlinePrevention)) {
            this._getInLineIntegrationIfExists();
          }
        },
        error: (error) => this._httpErrorHandlerService.handle(error),
      });
  }

  private _getViolationRules() {
    if (!(this.integrationId && this.policyId)) {
      this.isLoading.set(false);
      return;
    }

    this._storeService.integrations$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((integrations) => {
      this.integrationModel = integrations.find((integration) => integration.id === this.integrationId);
    });
    forkJoin([
      this._policiesHttpService.getPolicy(this.integrationId, this.policyId),
      this._violationRulesHttpService.getGovernanceRules(this.integrationId, this.policyId),
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([policyModel, violationRules]) => {
        this.policyModel = policyModel;

        const riskRules: ViolationRule[] = [];
        const hygieneRulesArray: ViolationRule[] = [];

        violationRules.forEach((rule) => {
          if (rule.category !== RuleCategory.hygiene) {
            riskRules.push(rule);
          } else {
            hygieneRulesArray.push(rule);
          }
        });

        if (
          this.featureFlagsService.isFeatureEnabled(
            FeatureFlagTypes.SpmIamDemoAmex
          )
        ) {
          this._prependIamDemoRules(riskRules);
        }
        this.violationRules.set(riskRules);
        this.hygieneRules.set(hygieneRulesArray);
        this.isLoading.set(false);
      });
  }

  private _prependIamDemoRules(
    riskRules: ViolationRule[]
  ): void {
    const mockRules: ViolationRule[] = [
      {
        id: 'ZN_HG00029',
        displayName: 'Agent action uses hard-coded credentials',
        description: 'Agent action uses hard-coded credentials.',
        severity: 'high' as never,
        category: 'leastPrivilege' as never,
        platform: 'custom_agent' as never,
        resourceType: 'agentAction' as never,
        owasp: ['LCNC-SEC-02', 'LLM-SEC-02', 'NHI-SEC-02'],
        attack: ['T1199'],
        status: 'on' as never,
        tags: ['generalAvailability', 'exploitable', 'AISPM'] as never[],
        summary: 'The agent action uses hard-coded credentials.',
        riskReason: '',
        triageSuggestion: '',
        fixSuggestion: '',
        riskLabels: [],
        cvssScores: [],
        effectivePolicySettings: [],
        integrationId: this.integrationId,
        policyId: this.policyId ?? '',
        isAvailable: true,
      },
      {
        id: 'ZN_NHI00005',
        displayName: 'Agent action authenticates with overprivileged service credentials',
        description: 'Agent action uses service credentials that grant broader access than required.',
        severity: 'high' as never,
        category: 'leastPrivilege' as never,
        platform: 'custom_agent' as never,
        resourceType: 'agentAction' as never,
        owasp: ['LCNC-SEC-02', 'LLM-SEC-05', 'NHI-SEC-05'],
        attack: ['T1078', 'T1530'],
        status: 'on' as never,
        tags: ['generalAvailability', 'AISPM', 'NHI'] as never[],
        summary: 'The salesforce_search action authenticates with overprivileged credentials.',
        riskReason: '',
        triageSuggestion: '',
        fixSuggestion: '',
        riskLabels: [],
        cvssScores: [],
        effectivePolicySettings: [],
        integrationId: this.integrationId,
        policyId: this.policyId ?? '',
        isAvailable: true,
      },
      {
        id: 'ZN_HG00043',
        displayName: 'Agent action has external communication capabilities',
        description: 'Agent action can communicate externally.',
        severity: 'high' as never,
        category: 'leastPrivilege' as never,
        platform: 'custom_agent' as never,
        resourceType: 'agentAction' as never,
        owasp: ['LCNC-SEC-04', 'NHI-SEC-05'],
        attack: [],
        status: 'on' as never,
        tags: ['generalAvailability', 'AISPM'] as never[],
        summary: 'The send_email tool operates with delegated Mail.Send permissions.',
        riskReason: '',
        triageSuggestion: '',
        fixSuggestion: '',
        riskLabels: [],
        cvssScores: [],
        effectivePolicySettings: [],
        integrationId: this.integrationId,
        policyId: this.policyId ?? '',
        isAvailable: true,
      },
      {
        id: 'ZN_HG00030',
        displayName: 'Agent action performs business critical operation with hard-coded credentials',
        description: 'Agent action performs business critical operations using hard-coded credentials.',
        severity: 'high' as never,
        category: 'leastPrivilege' as never,
        platform: 'custom_agent' as never,
        resourceType: 'agentAction' as never,
        owasp: ['LCNC-SEC-02', 'LLM-SEC-02', 'NHI-SEC-02', 'NHI-SEC-04', 'NHI-SEC-05'],
        attack: [],
        status: 'on' as never,
        tags: ['generalAvailability', 'AISPM'] as never[],
        summary: 'The salesforce_search tool authenticates using a hard-coded API key.',
        riskReason: '',
        triageSuggestion: '',
        fixSuggestion: '',
        riskLabels: [],
        cvssScores: [],
        effectivePolicySettings: [],
        integrationId: this.integrationId,
        policyId: this.policyId ?? '',
        isAvailable: true,
      },
      {
        id: 'ZN_HG00071',
        displayName: 'Agent is exposed to untrusted external content',
        description: 'Agent reads untrusted content without sanitization.',
        severity: 'high' as never,
        category: 'leastPrivilege' as never,
        platform: 'custom_agent' as never,
        resourceType: 'agent' as never,
        owasp: ['LLM-SEC-01', 'LLM-SEC-02'],
        attack: [],
        status: 'on' as never,
        tags: ['generalAvailability', 'AISPM'] as never[],
        summary: 'Sales Helper reads inbound emails without sanitization.',
        riskReason: '',
        triageSuggestion: '',
        fixSuggestion: '',
        riskLabels: [],
        cvssScores: [],
        effectivePolicySettings: [],
        integrationId: this.integrationId,
        policyId: this.policyId ?? '',
        isAvailable: true,
      },
      {
        id: 'ZN_HG00072',
        displayName: 'Agent tool provides access to sensitive business data',
        description: 'Agent tool retrieves sensitive business data.',
        severity: 'high' as never,
        category: 'leastPrivilege' as never,
        platform: 'custom_agent' as never,
        resourceType: 'agentAction' as never,
        owasp: ['LLM-SEC-06', 'NHI-SEC-05'],
        attack: [],
        status: 'on' as never,
        tags: ['generalAvailability', 'AISPM'] as never[],
        summary: 'The salesforce_search tool retrieves customer PII and pipeline data.',
        riskReason: '',
        triageSuggestion: '',
        fixSuggestion: '',
        riskLabels: [],
        cvssScores: [],
        effectivePolicySettings: [],
        integrationId: this.integrationId,
        policyId: this.policyId ?? '',
        isAvailable: true,
      },
    ];

    const existingIds = new Set(riskRules.map((r) => r.id));
    for (const rule of mockRules) {
      if (!existingIds.has(rule.id)) {
        riskRules.unshift(rule);
      }
    }
  }

  private _getEnvironments() {
    this._environmentsHttpService
      .getEnvironments(this.integrationId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((environmentsModel: EnvironmentModel[]) => {
        this.environments.set(environmentsModel);
      });
  }

  private _getInLineIntegrationIfExists() {
    this._inlineIntegrationsHttpService
      .getInLineIntegrationByRelatedIntegrationId(this.integrationId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((inlineIntegration: InLineIntegration) => {
        this.inlineIntegration = inlineIntegration;

        // Add in-line control rules tab if feature flag is enabled
        if (this.featureFlagsService.isFeatureEnabled(FeatureFlagTypes.InlinePrevention) && this.inlineIntegration) {
          this.tabs.push({ labelPath: 'policyPage.policy.inLineControlRulesTab', isBeta: false });
        }
      });
  }
}
